import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Box from "../Box/Box";
import Button from "../Button/Button";
import IconSvg from "../IconSvg/IconSvg";
import ProgressBar from "../ProgressBar/ProgressBar";
import Text from "../Text/Text";
import Loader from "../Loader/Loader";
import SparklesImage from "../assets/sparkles3.svg";
import { breakpoints } from "../../constants";
import { useMedias } from "../hooks/useMedia";

const Completed = ({completedCount, canCompleteMultipleTimes}) => {
  const text = canCompleteMultipleTimes ? 
    `Completed ${completedCount} ${completedCount === 1 ? "time" : "times"}` 
    : "Completed!";
  return (
    <Box flex alignItems="center" gap="6px">
      <IconSvg icon="filled-check-circle" width="18px" color="primary700" />
      <Text extraBold size="14px" lHeight="21px" color="secondary500">{text}</Text>
      {!canCompleteMultipleTimes && <IconSvg icon="cheers" width="18px" />}
    </Box>
  );
};

const Condition = ({icon, condition, conditionDescription, link, iconWidth = "14px", isMobile}) => {
  return (
    <Box flex alignItems={!isMobile && "center"} gap="8px" spacing="mb-2 mr-7+2" sm-spacing="mb-4">
      <IconSvg icon={icon} width={iconWidth} color="primary700" />
      <ConditionText
        color="secondary500"
        bold
        size="14px"
        lHeight="21px"
      >
        {condition}: {!link ? conditionDescription
          : (
            <Link target="_blank" href={link}>
              {conditionDescription}
            </Link>
          )}
      </ConditionText>
    </Box>
  );
};

const SnapshotActivity = ({
  id,
  isAuthenticated,
  onSignUp,
  refresh,
  getDetails,
}) => {
  const {isMobile} = useMedias();
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState(null);
  // const percent = (stepsCompleted.length / steps) * 100;
  const percent = 0;

  useEffect(() => {
    setLoading(true);
    getDetails(id)
      .then(({activity}) => setActivity(activity))
      .finally(() => {
        setLoading(false);
      });
  }, [refresh]);

  if(loading) {
    return (
      <Box flex justifyContent="center">
        <Loader />
      </Box>
    );
  }

  return (
    <>
      <Box flex relative direction={isMobile && "column"}>
                
        {activity?.completed && (
          <StyledImage src={SparklesImage} />
        )}
        <Box>
          <Text extraBold size="14px" lHeight="21px" color="secondary500" spacing="mb-4" sm-spacing="mb-5">
            Conditions:
          </Text>
          <ConditionsWrapper>
            {activity?.space && (
              <Condition
                icon="atom"
                condition="Space"
                conditionDescription={activity.space}
                link={activity.space_link}
                isMobile={isMobile}
              />
            )}
            {activity?.proposals_count && activity.proposals_count > 1 && (
              <Condition
                icon="person-carry-box"
                condition="Number of votes"
                conditionDescription={activity.proposals_count}
                iconWidth="16px"
                isMobile={isMobile}
              />
            )}
            {activity?.voting_power && (
              <Condition
                icon="bolt"
                condition="Minimum voting power"
                conditionDescription={activity.voting_power}
                isMobile={isMobile}
              /> 
            )}
            {activity?.minimum_votes && (
              <Condition
                icon="person-carry-box"
                condition="Proposal popularity"
                conditionDescription={"Minimum " + activity.minimum_votes + " votes"}
                isMobile={isMobile}
              /> 
            )}
            {activity?.role && (
              <Condition
                icon="astronaut"
                condition="Role"
                conditionDescription={activity.role}
                iconWidth="16px"
                isMobile={isMobile}
              /> 
            )}
            {activity?.start_date && (
              <Condition
                icon="clock"
                condition="Dates"
                iconWidth="16px"
                conditionDescription={`${activity.start_date} to ${activity.end_date}`}
                isMobile={isMobile}
              />
            )}
          </ConditionsWrapper>
        </Box>
        {!!activity?.rewards_earned && activity.rewards_earned > 0 && (
          <Box
            flex
            direction="column"
            alignItems={!isMobile ? "flex-end" : "center"}
            justifyContent="space-between"
            gap="2rem"
            spacing="ml-a"
            sm-spacing="mx-a"
          >
            <Box flex direction="column" gap="6px" alignItems={!isMobile ? "flex-end" : "center"}>
              <Text extraBold lHeight="21px" color="secondary500">Earned:</Text>
              <Box flex alignItems="center" gap="6px">
                <IconSvg icon="logo-gold-circle" width="16px" />
                <Text bold size="24px" lHeight="30px" color="accent550">{+activity?.rewards_earned} $THRIVE</Text>
              </Box>
              {/* <Text
                bold
                size="14px"
                lHeight="21px"
                color="#7EC0CE"
                underline
                pointer
                onClick={viewYourReceipt}>
                  View your receipt
              </Text> */}
            </Box>
            {!isMobile && (
              <Completed
                completedCount={activity?.contributions_count}
                canCompleteMultipleTimes={activity?.unlimited_contributions_per_user}
              />
            )}
          </Box>
        )}
      </Box>
      {(activity?.with_progress || !isAuthenticated) && <Line bgColor="tertiary200" spacing="mt-5" sm-spacing="mt-2" />}
      <Box relative flex direction={isMobile && "column"}>
        {(activity?.with_progress || !isAuthenticated) && (
          <ProgressBox isAuthenticated={isAuthenticated} flex direction="column">
            <Text extraBold size="14px" lHeight="21px" color="secondary500" spacing="mb-3">
              Progress toward succes:
            </Text>
            <Box flex alignItems="center" gap="16px" spacing="mb-a">
              <Box width="233px">
                <ProgressBar percent={!isAuthenticated ? 60 :  percent} bgColor="#5FB974" height="6" />
              </Box>
              <Box flex alignItems="center" gap="6px">
                <Text extraBold color="secondary400" lHeight="24px">
                  {!!isAuthenticated && `${activity?.contributions_count}/${activity?.contributions_per_user}`}
                </Text>
                <IconSvg icon="help-circle" width="14px" color="primary700" />
              </Box>
            </Box>
          </ProgressBox>
        )}
        {isMobile && isAuthenticated && activity?.completed && <Box height="1px" bgColor="#E9E9E9" spacing="my-5" />}
        {!isAuthenticated && (
          <SignUpWrapper>
            <Button
              bold
              height={!isMobile && "56px"}
              bgColor="primary700"
              borderRadius="40px"
              onClick={onSignUp}
            >
                Sign Up to Check Your Progress and Get Rewarded
            </Button>
          </SignUpWrapper>
        )}
      </Box>
    </>
  );
};


const ConditionsWrapper = styled(Box)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-auto-flow: column;
  grid-template-rows: repeat(3, auto);

  @media ${breakpoints.mobile} {
    display: flex;
    flex-direction: column;
  }
`;

const Line = styled(Box)`
  height: 1px;
  margin-bottom: 21px;
`;

const ProgressBox = styled(Box)`
  ${({isAuthenticated}) => !isAuthenticated && `filter: blur(5px);`}
`;

const SignUpWrapper = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);

  @media ${breakpoints.mobile} {
    left: 20px;
    right: 20px;
    transform: translate(0, -50%);
  }
`;

const StyledImage = styled.img`
  position: absolute;
  top: 50%;
  right: 185px;
  width: 199px;
  transform: translate(0, -50%);
  height: 80%;
`;

const ConditionText = styled(Text)`
  flex: 1;
`;

const Link = styled.a`
  text-decoration: none;
  color: #88CFDE;
`;

export default SnapshotActivity;